import { Newspaper, CheckCircle, Clock, NotePencil } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { TherapistBlogPost } from "@/types";

interface Props {
  posts: TherapistBlogPost[];
  isLoading?: boolean;
}

export function BlogSummary({ posts, isLoading = false }: Props) {
  const counts = posts.reduce(
    (acc, p) => {
      acc[p.status] += 1;
      return acc;
    },
    { draft: 0, pending: 0, published: 0, rejected: 0 } as Record<TherapistBlogPost["status"], number>,
  );
  const v = (n: number) => (isLoading ? "..." : String(n));

  return (
    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryTile
        icon={Newspaper}
        iconClass="text-foreground/70"
        label="Ukupno"
        value={v(posts.length)}
        hint="članaka ukupno"
      />
      <SummaryTile
        icon={CheckCircle}
        iconClass="text-emerald-600"
        label="Objavljeni"
        value={v(counts.published)}
        hint="vidljivi javno"
      />
      <SummaryTile
        icon={Clock}
        iconClass="text-primary"
        label="Na čekanju"
        value={v(counts.pending)}
        hint="čeka pregled"
        accent={!isLoading && counts.pending > 0}
      />
      <SummaryTile
        icon={NotePencil}
        iconClass="text-muted-foreground/60"
        label="Nacrti"
        value={v(counts.draft)}
        hint="nije poslano"
      />
    </section>
  );
}

function SummaryTile({
  icon: Icon,
  iconClass,
  label,
  value,
  hint,
  accent = false,
}: {
  icon: typeof Newspaper;
  iconClass: string;
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm",
        accent && "border-amber-200 bg-amber-50/60",
      )}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className={cn("size-4", accent ? "text-amber-700" : iconClass)} />
        <span>{label}</span>
      </div>
      <p className="mt-1.5 text-2xl font-bold tabular-nums text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      <p className="truncate text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
